import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Suspense, useState, useEffect } from 'react';
import { lazyPage } from './utils/chunkReload';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { SystemConfigProvider } from './contexts/SystemConfigContext';
import { AccessibilityProvider } from './contexts/AccessibilityContext';
import ErrorBoundary from './components/common/ErrorBoundary';
import OfflineIndicator from './components/common/OfflineIndicator';
import Login from './components/common/Login';
import SetupPassword from './pages/SetupPassword';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import PrivacyNotice from './components/common/PrivacyNotice';
import Support from './components/common/Support';
import { APP } from './config/app';
import { getSetupStatus, setupNeeded } from './services/setup.service';
import { useAuth } from './contexts/AuthContext';
import './styles/theme.css';
import './styles/mobile-touch-targets.css';
import './styles/smooth-animations.css';
import './styles/accessibility.css';
import './components/common/PageTransition.css';

// Lazy-loaded portal layouts for code splitting
const FirstRunWizard = lazyPage(() => import('./components/first-run/FirstRunWizard'));
const StudentLayout = lazyPage(() => import('./portals/student/StudentLayout'));
const StaffLayout = lazyPage(() => import('./portals/staff/StaffLayout'));
const AdminLayout = lazyPage(() => import('./portals/admin/AdminLayout'));
const HoDLayout = lazyPage(() => import('./portals/hod/HoDLayout'));
const ViewOnlyStaffLayout = lazyPage(() => import('./portals/view-only-staff/ViewOnlyStaffLayout'));
const AccountsOfficerLayout = lazyPage(() => import('./portals/accounts-officer/AccountsOfficerLayout'));
const ITSupportLayout = lazyPage(() => import('./portals/it-support/ITSupportLayout'));

// Lazy-loaded demo portals (public routes)

// Portal loading fallback component
function PortalLoadingFallback() {
  return (
    <div className="loading-container">
      <div className="loading-spinner"></div>
      <p>Loading portal...</p>
    </div>
  );
}

// Route guard: redirects to /first-run when setup is incomplete and no users exist
function SetupGuard({ children }) {
  const [checked, setChecked] = useState(false);
  const [needsSetup, setNeedsSetup] = useState(false);

  useEffect(() => {
    getSetupStatus()
      .then((status) => {
        setNeedsSetup(setupNeeded(status));
        setChecked(true);
      })
      .catch(() => setChecked(true)); // Don't block the app if status check fails
  }, []);

  if (!checked) return <PortalLoadingFallback />;
  if (needsSetup) return <Navigate to="/first-run" replace />;
  return children;
}

// The wizard itself, only while the system still needs it. On a live system
// /first-run used to render all eleven steps for anyone and fail only at
// Deploy (audit A, L2); now it goes to sign-in. A failed status check counts
// as "already set up", the same default SetupGuard uses.
function FirstRunGuard({ children }) {
  const [state, setState] = useState('checking'); // checking | wizard | live

  useEffect(() => {
    getSetupStatus()
      .then((status) => setState(setupNeeded(status) ? 'wizard' : 'live'))
      .catch(() => setState('live'));
  }, []);

  if (state === 'checking') return <PortalLoadingFallback />;
  if (state === 'live') return <Navigate to="/" replace />;
  return children;
}

/**
 * Map a real user role to its default portal home (used to bounce a user who
 * lands on a portal they aren't permitted to see).
 */
function roleHome(role) {
  switch (role) {
    case 'student': return '/student';
    case 'staff': return '/staff';
    case 'department_admin': return '/admin';
    case 'head_of_department': return '/hod';
    case 'master_admin': return '/admin';
    case 'view_only_staff': return '/view-only-staff';
    case 'accounts_officer': return '/accounts-officer';
    case 'it_support': return '/it-support';
    default: return '/';
  }
}

/**
 * Route guard: redirects to login when unauthenticated, and (when allowedRoles
 * is given) bounces an authenticated user whose REAL role isn't permitted back
 * to their own portal. Decisions use user.role only — never the spoofable
 * sessionStorage viewAsRole — and admin roles are included in the lower portals'
 * allow-lists so the legitimate "View as" flow still works.
 * Waits for AuthContext to finish loading before deciding.
 */
function ProtectedRoute({ children, allowedRoles }) {
  const { user, loading } = useAuth();
  if (loading) return <PortalLoadingFallback />;
  if (!user) return <Navigate to="/" replace />;
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to={roleHome(user.role)} replace />;
  }
  return children;
}

function ViewAsBanner() {
  const { viewAsRole, stopViewAs, user } = useAuth();
  if (!viewAsRole) return null;

  const adminPath = user?.role === 'head_of_department' ? '/hod'
    : '/admin';
  // startViewAs is only ever called with 'student' or 'staff'.
  const roleLabel = viewAsRole === 'student' ? 'Student' : 'Staff';
  const backLabel = user?.role === 'master_admin' ? 'Back to Master Admin'
    : user?.role === 'head_of_department' ? 'Back to HoD'
    : 'Back to Admin';

  return (
    <>
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999,
        background: 'var(--color-primary)',
        color: 'var(--text-inverse)', padding: '8px 16px',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px',
        fontSize: '14px', fontWeight: 600,
        boxShadow: 'var(--shadow-md)',
      }}>
        <span>Viewing as {roleLabel} — changes you make here are real</span>
        <button
          onClick={() => { stopViewAs(); window.location.href = adminPath; }}
          style={{
            background: 'var(--surface-base)', color: 'var(--text-primary)', border: 'none',
            padding: '4px 14px', borderRadius: '4px', cursor: 'pointer',
            fontWeight: 600, fontSize: '13px',
          }}
        >
          {backLabel}
        </button>
      </div>
      {/* Spacer to push content below the fixed banner */}
      <div style={{ height: '40px' }} />
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <SystemConfigProvider>
          <AccessibilityProvider>
          <OfflineIndicator />
          <Router basename={APP.basePath}>
            <ViewAsBanner />
            <Suspense fallback={<PortalLoadingFallback />}>
              <Routes>
                {/* First-run setup wizard — outside SetupGuard to avoid redirect loop,
                    behind its own guard so a live system sends it to sign-in */}
                <Route path="/first-run" element={<FirstRunGuard><FirstRunWizard /></FirstRunGuard>} />

                {/* Public: Privacy notice (GDPR Article 13/14) */}
                <Route path="/privacy" element={<PrivacyNotice />} />

                {/* Public: Support page — the Support URL on the App Store listing.
                    Outside SetupGuard so it answers even when the system is not set up. */}
                <Route path="/support" element={<Support />} />

                {/* All other routes — guarded against uninitialized systems */}
                <Route path="/*" element={
                  <SetupGuard>
                    <Routes>
                      {/* Home - Portal Selection */}
                      <Route path="/" element={<Login />} />

                {/* Public: password setup via emailed link */}
                <Route path="/setup-password/:token" element={<SetupPassword />} />

                {/* Public: password reset flow */}
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />

                {/* Main Portals — require authentication AND the correct role.
                    Admin roles are included in the lower portals so "View as" works. */}
                <Route path="/student/*" element={<ProtectedRoute allowedRoles={['student', 'staff', 'department_admin', 'head_of_department', 'master_admin']}><StudentLayout /></ProtectedRoute>} />
                <Route path="/staff/*" element={<ProtectedRoute allowedRoles={['staff', 'department_admin', 'head_of_department', 'master_admin']}><StaffLayout /></ProtectedRoute>} />
                <Route path="/admin/*" element={<ProtectedRoute allowedRoles={['department_admin', 'master_admin']}><AdminLayout /></ProtectedRoute>} />
                <Route path="/hod/*" element={<ProtectedRoute allowedRoles={['head_of_department', 'master_admin']}><HoDLayout /></ProtectedRoute>} />
                <Route path="/view-only-staff/*" element={<ProtectedRoute allowedRoles={['view_only_staff', 'department_admin', 'head_of_department', 'master_admin']}><ViewOnlyStaffLayout /></ProtectedRoute>} />
                <Route path="/accounts-officer/*" element={<ProtectedRoute allowedRoles={['accounts_officer', 'department_admin', 'head_of_department', 'master_admin']}><AccountsOfficerLayout /></ProtectedRoute>} />
                <Route path="/it-support/*" element={<ProtectedRoute allowedRoles={['it_support', 'department_admin', 'head_of_department', 'master_admin']}><ITSupportLayout /></ProtectedRoute>} />

                    </Routes>
                  </SetupGuard>
                } />
              </Routes>
            </Suspense>
          </Router>
          </AccessibilityProvider>
          </SystemConfigProvider>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
